import React from 'react';
import ReactPlayer from 'react-player';

import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Chip } from '@tmap/mmm-style-guide/src/Chip';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import imageUrlBuilder from '@sanity/image-url';
import { PlayButton } from '@tmap/mmm-style-guide/src/Icon';

import PreloadablePicture from '../../preloadablePicture';
import client from '../../../client';

const builder = imageUrlBuilder(client);

const [PrimaryPic, PrimaryImg] = [PreloadablePicture, 'img'].map((component) => (
  styled(component)(({ theme }) => ({
    width: '100%',
    minHeight: '100%',
    height: 'auto',
    '&': theme.responsiveProperty('borderRadius', { xs: '0px', sm: '8px' }),
  }))
));

const [SecondaryPic, SecondaryImg] = [PreloadablePicture, 'img'].map((component) => (
  styled(component)(() => ({
    height: '100%',
    width: '100%',
    borderRadius: '8px',
  }))
));

const MediaCounter = styled(Chip)(({ theme }) => ({
  span: {
    overflow: 'unset',
  },
  position: 'absolute',
  bottom: theme.spacing(1),
  right: theme.spacing(1),
  cursor: 'pointer',
  fontSize: 13,
  borderRadius: '4px',
}));

const MobileCounter = styled(Chip)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
  span: {
    overflow: 'unset',
  },
  position: 'absolute',
  bottom: theme.spacing(1),
  right: theme.spacing(1),
  cursor: 'pointer',
  fontSize: 13,
  borderRadius: '4px',
  backgroundColor: theme.palette.grey[700],
  color: theme.palette.inverted.contrastText,
}));

const PrimaryContainer = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    minHeight: '360px',
  },
  position: 'relative',
  '.react-player__preview': {
    cursor: 'zoom-in !important',
    position: 'relative',
    picture: {
      height: '100%',
      img: {
        [theme.breakpoints.down('sm')]: {
          height: '240px',
          objectFit: 'cover',
        },
      },
    },
  },
}));

const SecondaryContainer = styled(Grid)(({ theme }) => ({
  position: 'relative',
  '.react-player__preview': {
    cursor: 'zoom-in !important',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const PlayIcon = styled(PlayButton)(({ theme }) => ({
  fontSize: '96px',
  position: 'absolute',
  [theme.breakpoints.down('md')]: {
    fontSize: '64px',
  },
}));

function PrimaryMedia(props) {
  const {
    media,
  } = props;

  const [preview, alt] = media._type === 'videoBlock' ? [media.previewImage, media.caption] : [media, media.alt];

  const primaryPic = preview && (
    <PrimaryPic preload>
      <source
        srcSet={builder.image(preview).format('webp').width(1440).height(865)
          .url()}
        width={1440}
        height={865}
        media='(min-width: 900px)'
      />
      <source
        srcSet={builder.image(preview).format('webp').width(470).height(570)
          .url()}
        width={470}
        height={570}
        media='(min-width: 600px) and (max-width: 899px)'
      />
      <source
        srcSet={builder.image(preview).format('webp').width(375).height(300)
          .url()}
        width={375}
        height={300}
        media='(max-width: 599px)'
      />
      <PrimaryImg
        src={builder.image(preview).width(1440).height(865).url()}
        width={1440}
        height={865}
        alt={alt}
      />
    </PrimaryPic>
  );

  if (media._type === 'videoBlock') {
    return (
      <Box sx={{ height: { xs: '240px', sm: '100%' } }}>
        <ReactPlayer
          src={media.url}
          light={primaryPic || true}
          playIcon={<PlayIcon />}
          width='100%'
          height='100%'
          wrapper='div'
        />
      </Box>
    );
  }

  return primaryPic;
}

function SecondaryMedia(props) {
  const {
    media,
    ...otherProps
  } = props;

  const [preview, alt] = media._type === 'videoBlock' ? [media.previewImage, media.caption] : [media, media.alt];
  const secondaryPic = preview && (
    <SecondaryPic preload {...otherProps}>
      <source srcSet={builder.image(preview).format('webp').width(400).height(240)
        .url()}
      />
      <SecondaryImg
        src={builder.image(preview).width(400).height(240).url()}
        width={400}
        height={240}
        alt={alt}
      />
    </SecondaryPic>
  );

  if (media._type === 'videoBlock') {
    return (
      <ReactPlayer
        src={media.url}
        light={secondaryPic || true}
        playIcon={<PlayIcon />}
        width='100%'
        height='100%'
        wrapper='div'
      />
    );
  }

  return secondaryPic;
}

function MultiImages(props) {
  const { images } = props;
  return (
    <>
      <PrimaryContainer item md={8} sm={6} xs={12}>
        <PrimaryMedia media={images[0]} />
        <MobileCounter variant='pill' label={`1 / ${images.length}`} />
      </PrimaryContainer>
      <SecondaryContainer item md={4} sm={6}>
        <Grid container flexDirection='column' spacing={2} sx={{ height: '100%', flexWrap: 'nowrap' }}>
          <Grid item sx={{ minHeight: 'calc(50% + 8px)' }}>
            <SecondaryMedia media={images[1]} />
          </Grid>
          <Grid item sx={{ minHeight: 'calc(50% + 8px)' }}>
            <SecondaryMedia media={images[2]} />
            <MediaCounter variant='pill' label={`View ${images.length} photos`} />
          </Grid>
        </Grid>
      </SecondaryContainer>
    </>
  );
}

export default MultiImages;
